import { useEffect, useState } from "react";
import { Badge } from "@zweb/ui";
import { apiFetch } from "../lib/api";

interface Lead {
  id: string;
  name: string;
  email: string;
  company: string | null;
  status: string;
  estimatedValue: number | null;
  tags: string | null;
  createdAt: string;
}

interface Pipeline {
  lead: Lead[];
  contact: Lead[];
  proposal: Lead[];
  negotiation: Lead[];
  client: Lead[];
  lost: Lead[];
}

const stageLabels: Record<string, string> = {
  lead: "Leads",
  contact: "Contato",
  proposal: "Proposta",
  negotiation: "Negociação",
  client: "Cliente",
  lost: "Perdido",
};

const statusLabels: Record<string, string> = {
  new: "Novo",
  contacted: "Contatado",
  qualified: "Qualificado",
  proposal_sent: "Proposta Enviada",
  negotiating: "Negociando",
  won: "Ganho",
  lost: "Perdido",
};

const statusColors: Record<string, string> = {
  new: "bg-accent-100 text-accent-800",
  contacted: "bg-yellow-100 text-yellow-800",
  qualified: "bg-green-100 text-green-800",
  proposal_sent: "bg-accent-100 text-accent-800",
  negotiating: "bg-orange-100 text-orange-800",
  won: "bg-green-100 text-green-800",
  lost: "bg-red-100 text-red-800",
};

export default function CRMPage() {
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [draggedLead, setDraggedLead] = useState<Lead | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    apiFetch("/api/crm/pipeline")
      .then((json) => {
        if (json.success) {
          setPipeline(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleDragStart = (lead: Lead) => {
    setDraggedLead(lead);
  };

  const handleDragOver = (e: React.DragEvent, stage: string) => {
    e.preventDefault();
    setDragOverStage(stage);
  };

  const handleDrop = async (stage: string) => {
    if (!draggedLead) return;

    try {
      await apiFetch(`/api/crm/leads/${draggedLead.id}/stage`, {
        method: "PUT",
        body: JSON.stringify({ stage: stage }),
      });
      window.location.reload();
    } catch (error) {
      console.error("Erro ao mover lead:", error);
    }

    setDraggedLead(null);
    setDragOverStage(null);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  if (!pipeline) {
    return null;
  }

  const stages = ["lead", "contact", "proposal", "negotiation", "client", "lost"];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/dashboard" className="text-sm font-medium hover:text-accent-600">Dashboard</a>
            <a href="/crm" className="text-sm font-medium text-accent-600">CRM</a>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                window.location.href = "/";
              }}
              className="text-sm text-red-600 hover:underline"
            >
              Sair
            </button>
          </nav>
        </div>
      </header>

      <main className="container py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">CRM - Pipeline</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Arraste e solte os leads entre os estágios
            </p>
          </div>
          <div className="flex gap-4">
            <a
              href="/crm/agenda"
              className="rounded-lg border border-gray-300 px-4 py-2 font-medium hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-900"
            >
              Agenda
            </a>
            <a
              href="/crm/historico"
              className="rounded-lg border border-gray-300 px-4 py-2 font-medium hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-900"
            >
              Histórico
            </a>
          </div>
        </div>

        <div className="mt-8 flex gap-4 overflow-x-auto pb-4">
          {stages.map((stage) => {
            const leads = pipeline[stage as keyof Pipeline] || [];
            return (
              <div
                key={stage}
                className={`min-w-[300px] flex-shrink-0 rounded-2xl border-2 p-4 ${
                  dragOverStage === stage
                    ? "border-accent-500 bg-accent-50 dark:bg-accent-900/20"
                    : "border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
                }`}
                onDragOver={(e) => handleDragOver(e, stage)}
                onDrop={() => handleDrop(stage)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{stageLabels[stage]}</h3>
                  <Badge variant="secondary">{leads.length}</Badge>
                </div>

                <div className="mt-4 space-y-3">
                  {leads.map((lead) => (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={() => handleDragStart(lead)}
                      className="cursor-move rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-950"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium">{lead.name}</div>
                          {lead.company && (
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {lead.company}
                            </div>
                          )}
                        </div>
                        <Badge className={statusColors[lead.status]}>
                          {statusLabels[lead.status]}
                        </Badge>
                      </div>

                      {lead.estimatedValue && (
                        <div className="mt-2 text-sm font-medium text-green-600">
                          {formatCurrency(lead.estimatedValue)}
                        </div>
                      )}

                      {lead.tags && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {JSON.parse(lead.tags).map((tag: string) => (
                            <span
                              key={tag}
                              className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="mt-3 text-xs text-gray-500">
                        {new Date(lead.createdAt).toLocaleDateString("pt-BR")}
                      </div>
                    </div>
                  ))}

                  {leads.length === 0 && (
                    <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-gray-700">
                      Arraste leads aqui
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
